import { SettingsSort, WindowSidebarRightBlank } from '@griply/assets';
import { FilterDocument } from '@griply/types';
import {
  buildUpcomingDefaultFilter,
  mapPartialToUpcomingSettings,
  upcomingFilters,
  upcomingGroupOptions,
  upcomingSortOptions,
} from '@griply/utils';
import { format } from 'date-fns';
import { useGoals } from 'features/goals/hooks/use-goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarUpcoming14, TodoList214 } from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FilterButton } from 'shared/components/ui/filter-button';
import {
  Colors as IconButtonColors,
  IconButton,
} from 'shared/components/ui/icon-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { TimeframeNavigationButtons } from 'shared/components/ui/timeframe-navigation-buttons';
import { useToday } from 'shared/contexts/today';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes/paths';
import { Timestamp } from 'shared/types/timestamp';
import {
  CalendarViewOptions,
  UpcomingViewOptions,
} from 'shared/types/upcoming-view-options';
import { useTheme } from 'styled-components';

import { useUpcomingFilterDocument } from '../../hooks/use-upcoming-filter-document';
import { MainContextMenu } from './main-context-menu';
import * as Styled from './page-top-bar.style';

export type PageTopBarProps = {
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  sidePanelOpen?: boolean;
  onToggleSidePanel: () => void;
  selectedView: UpcomingViewOptions;
  changeSelectedView: (viewOption: UpcomingViewOptions) => void;
  selectedCalendarView: CalendarViewOptions;
  changeCalendarView: (viewOption: CalendarViewOptions) => void;
  referenceDate?: Timestamp;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  onPrev,
  onToday,
  onNext,
  sidePanelOpen,
  onToggleSidePanel,
  selectedView,
  changeSelectedView,
  selectedCalendarView,
  changeCalendarView,
  referenceDate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const activePath = useActivePath({ level: 2 });
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const today = useToday();
  const updateUserSettings = useUpdateUserSettings();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useSortedTags();
  const filterDocument = useUpcomingFilterDocument();
  const defaultFilterDocument = useMemo(
    () => buildUpcomingDefaultFilter(today),
    [today],
  );

  const updateFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) => {
      const settings = mapPartialToUpcomingSettings(newFilterDocument);
      if (settings) {
        updateUserSettings(settings);
      }
    },
    [updateUserSettings],
  );

  const onReset = useCallback(() => {
    const settings = mapPartialToUpcomingSettings({ filters: null });
    if (settings) {
      updateUserSettings(settings);
    }
  }, [updateUserSettings]);

  const viewOptions = useMemo<Option<UpcomingViewOptions>[][]>(
    () => [
      [
        {
          icon: TodoList214,
          value: UpcomingViewOptions.List,
        },
        {
          icon: CalendarUpcoming14,
          value: UpcomingViewOptions.Calendar,
        },
      ],
    ],
    [],
  );

  const calendarViewOptions = useMemo<Option<CalendarViewOptions>[][]>(
    () => [
      [
        {
          label: '4D',
          value: CalendarViewOptions.FourDay,
        },
        {
          label: 'W',
          value: CalendarViewOptions.Week,
        },
        {
          label: 'M',
          value: CalendarViewOptions.Month,
        },
      ],
    ],
    [],
  );

  const pageTitle = useMemo(
    () =>
      referenceDate ? format(referenceDate, 'MMMM') : t('pages.upcoming.title'),
    [referenceDate, t],
  );

  return (
    <TopBarBase
      borderColor={
        selectedView === UpcomingViewOptions.Calendar
          ? theme.palette.background.default
          : undefined
      }
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>{pageTitle}</Styled.Title>

        <Styled.CenterInteractions>
          <SliderButtons
            optionGroups={viewOptions}
            onOption={changeSelectedView}
            value={selectedView}
            preventDrag
          />

          {selectedView === UpcomingViewOptions.Calendar && (
            <>
              <SliderButtons
                optionGroups={calendarViewOptions}
                onOption={changeCalendarView}
                value={selectedCalendarView}
                preventDrag
              />

              <TimeframeNavigationButtons
                onPrev={onPrev}
                onToday={onToday}
                onNext={onNext}
              />
            </>
          )}
        </Styled.CenterInteractions>

        <Styled.Interactions $selected={!!sidePanelOpen}>
          {!theme.isMobile && selectedView === UpcomingViewOptions.Calendar && (
            <Button
              onClick={onToggleSidePanel}
              size={Sizes.Small}
              variant={Variants.Text}
              color={Colors.TextSecondary}
              endIcon={WindowSidebarRightBlank}
              iconStrokeWidth={2}
              selected={sidePanelOpen}
              preventDrag
            >
              {t('pages.upcoming.nav-bar.all-tasks.label')}
            </Button>
          )}
          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={defaultFilterDocument}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            onReset={onReset}
            supportedFilters={upcomingFilters}
            supportedSortingModes={
              selectedView === UpcomingViewOptions.Calendar
                ? []
                : upcomingSortOptions
            }
            supportedGroupByModes={upcomingGroupOptions}
            renderAsIconButton
            preventDrag
          />
          <IconButton
            ref={buttonRef}
            icon={SettingsSort}
            strokeWidth={2}
            color={IconButtonColors.Default}
            onClick={openMenu}
            preventDrag
          />
          {menuOpen && (
            <MainContextMenu onClose={closeMenu} referenceElement={buttonRef} />
          )}
        </Styled.Interactions>
      </Styled.Container>
    </TopBarBase>
  );
};
